import { Component, ElementRef, forwardRef, HostBinding, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import { ModeView } from '../../../shared/ontology/model/mode-view.enum';
import { Category } from '../../../shared/ontology/model/category';
import { isEditableByMode } from '../utils/ontology-editing.util';

@Component({
    selector: 'jhi-categories',
    template: `
		@if (isEditable()) {
			<div *ngFor="let c of categories; let i = index; trackBy: trackByIndex" class="rounded p-2 mb-2">
				<div class="row g-2 align-items-center">
					<div class="col-sm-4">
						<input type="text" class="form-control"
							[class.is-invalid]="isNameInvalid(i)"
							[disabled]="disabled || isDisabled(c)"
							[value]="c?.name ?? ''"
							(input)="onNameInput(i, $event)"
							(blur)="onRowBlur(i)"
							[attr.maxlength]="nameMaxLength"
							[attr.pattern]="pattern || null"
							[attr.placeholder]="'ontology-manager.tab.scales.modal.categories.name' | translate"
						/>
					</div>

					<div class="col-sm-7">
						<input type="text" class="form-control"
							[class.is-invalid]="isDescriptionInvalid(i)"
							[disabled]="disabled || isDisabled(c)"
							[value]="c?.description ?? ''"
							(input)="onDescriptionInput(i, $event)"
							(blur)="onRowBlur(i)"
							[attr.maxlength]="descriptionMaxLength"
							[attr.pattern]="pattern || null"
							[attr.placeholder]="'ontology-manager.tab.scales.modal.categories.description' | translate"
						/>
					</div>
					<div class="col-sm-1 text-end" *ngIf="!disabled && isRemovable(c)">
						<button type="button" class="btn text-danger" (click)="remove($event, i, c)">
							<span class="fa fa-trash"></span>
						</button>
					</div>
				</div>
			</div>
			<div>
				<button
					type="button"
					class="btn btn-outline-info btn-sm"
					(click)="addAndScrollToBottom($event)"
					[disabled]="disabled">
					<span class="fa fa-plus"></span>
					{{ 'ontology-manager.tab.scales.modal.categories.add.another.category' | translate }}
				</button>
			</div>
		} @else {
			<table class="table table-condensed">
				<thead>
				<tr>
					<th>{{ 'ontology-manager.tab.scales.modal.categories.name' | translate }}</th>
					<th>{{ 'ontology-manager.tab.scales.modal.categories.description' | translate }}</th>
				</tr>
				</thead>
				<tbody>
				<tr *ngFor="let c of categories; trackBy: trackByIndex">
					<td>
						<truncate-span [textInput]="c?.name" [maxLength]="10"></truncate-span>
					</td>
					<td>
						<truncate-span [textInput]="c?.description" [maxLength]="78"></truncate-span>
					</td>
				</tr>
				</tbody>
			</table>
		}
    `,
    styles: [
        `
            :host {
                display: block;
            }

            a.disabled {
                pointer-events: none;
                opacity: 0.5;
            }

            :host input.form-control.is-invalid {
                border-left: 6px solid var(--bs-danger);
                padding-left: calc(.75rem - 6px);
            }
        `
    ],
    providers: [
        { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => CategoriesComponent), multi: true },
        { provide: NG_VALIDATORS, useExisting: forwardRef(() => CategoriesComponent), multi: true }
    ]
})
export class CategoriesComponent implements ControlValueAccessor, Validator, OnChanges {
    @ViewChild('scrollContainer') scrollContainer?: ElementRef<HTMLElement>;

    @Input() modeView: ModeView = ModeView.VIEW;
    @Input() model?: any;
    @Input() property?: string;
    @Input() disabled = false;

    private _pattern: string | null = null;
    private _nameMaxLength = 200;
    private _descriptionMaxLength = 255;

    @Input()
    set pattern(value: string | null) {
        this._pattern = value;
        this.validatorChangeFn();
    }

    get pattern(): string | null {
        return this._pattern;
    }

    @Input()
    set nameMaxLength(value: number) {
        this._nameMaxLength = value;
        this.validatorChangeFn();
    }

    get nameMaxLength(): number {
        return this._nameMaxLength;
    }

    @Input()
    set descriptionMaxLength(value: number) {
        this._descriptionMaxLength = value;
        this.validatorChangeFn();
    }

    get descriptionMaxLength(): number {
        return this._descriptionMaxLength;
    }

    private categoryList: Category[] = [];
    private rowActivated: boolean[] = [];

    private onChange: (v: Category[]) => void = () => {
    };
    private onTouched: () => void = () => {
    };
    private validatorChangeFn: () => void = () => {
    };

    @HostBinding('class.modeView-create')
    public get isCreateMode(): boolean {
        return this.modeView === ModeView.CREATE;
    }

    get categories(): Category[] {
        return this.categoryList ?? [];
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.isEditable()) {
            this.ensureInit();
        }
        this.validatorChangeFn();
    }

    writeValue(v: Category[] | null): void {
        this.categoryList = v ?? [];
        this.rowActivated = new Array(this.categoryList.length).fill(false);

        if (this.isEditable()) {
            this.ensureInit();
        }

        this.validatorChangeFn();
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    registerOnValidatorChange(fn: () => void): void {
        this.validatorChangeFn = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    public markTouched(): void {
        this.onTouched();
    }

    public isEditable(): boolean {
        return isEditableByMode({
            modeView: this.modeView,
            model: this.model,
            property: this.property ?? ''
        });
    }

    public trackByIndex(index: number): number {
        return index;
    }

    public addAndScrollToBottom(e: Event): void {
        e.preventDefault();
        this.ensureInit();

        this.categoryList.push({  name: '', description: '', editable: true } as Category);

        this.rowActivated.push(true);
        this.onChange(this.categoryList);
        this.markTouched();
        this.validatorChangeFn();

        queueMicrotask(() => this.scrollToBottom());
    }

    public remove(e: Event, index: number, c: Category): void {
        e.preventDefault();

        if (!this.isRemovable(c)) {
            return;
        }

        this.categoryList.splice(index, 1);
        this.rowActivated.splice(index, 1);

        this.onChange(this.categoryList);
        this.markTouched();
        this.validatorChangeFn();
    }

    public onNameInput(i: number, event: Event): void {
        const target = event.target as HTMLInputElement | null;
        if (!target) {
            return;
        }

        this.ensureInit();
        this.activateRow(i);

        if (!this.categoryList[i]) {
            this.categoryList[i] = { name: '', description: '', editable: true } as Category;
        }

        this.categoryList[i].name = target.value ?? '';
        this.onChange(this.categoryList);
        this.validatorChangeFn();
    }

    public onDescriptionInput(i: number, event: Event): void {
        const target = event.target as HTMLInputElement | null;
        if (!target) {
            return;
        }

        this.ensureInit();
        this.activateRow(i);

        if (!this.categoryList[i]) {
            this.categoryList[i] = { name: '', description: '', editable: true } as Category;
        }

        this.categoryList[i].description = target.value ?? '';
        this.onChange(this.categoryList);
        this.validatorChangeFn();
    }

    public onRowBlur(i: number): void {
        this.activateRow(i);
        this.markTouched();
    }

    public isRemovable(c: Category): boolean {
        return this.categoryList.length >= 2 && c?.editable === true;
    }

    public isDisabled(c: Category): boolean {
        return c?.editable === false;
    }

    private activateRow(i: number): void {
        this.rowActivated[i] = true;
    }

    private ensureInit(): void {
        this.categoryList = this.categoryList ?? [];

        if (this.categoryList.length === 0) {
            this.categoryList.push({ name: '', description: '', editable: true } as Category);
            this.rowActivated = [true];
            this.onChange(this.categoryList);
        }
    }

    public validate(control: AbstractControl): ValidationErrors | null {
        if (this.modeView === ModeView.VIEW) {
            return null;
        }

        const categories = (control.value ?? []) as Category[];

        const names = new Set<string>();
        const descriptions = new Set<string>();

        for (const c of categories) {
            const name = (c?.name ?? '').trim();
            const desc = (c?.description ?? '').trim();

            if (!name || !desc) {
                return { emptyValue: true };
            }

            if (name.length > this.nameMaxLength || desc.length > this.descriptionMaxLength) {
                return { maxlength: true };
            }

            if (this.pattern) {
                const re = new RegExp(this.pattern);
                if (!re.test(name) || !re.test(desc)) {
                    return { pattern: true };
                }
            }

            const normalizedName = this.norm(name);
            const normalizedDesc = this.norm(desc);

            if (names.has(normalizedName)) {
                return { nonUniqueName: true };
            }

            if (descriptions.has(normalizedDesc)) {
                return { nonUniqueValue: true };
            }

            names.add(normalizedName);
            descriptions.add(normalizedDesc);
        }

        return null;
    }

    private scrollToBottom(): void {
        const el = this.scrollContainer?.nativeElement;
        if (!el) {
            return;
        }
        el.scrollTop = el.scrollHeight;
    }

    private norm(v: any): string {
        return String(v ?? '').trim().toLowerCase();
    }

    private nameCounts(): Map<string, number> {
        const map = new Map<string, number>();

        for (const c of this.categoryList ?? []) {
            const key = this.norm(c?.name);
            if (key.length === 0) {
                continue;
            }
            map.set(key, (map.get(key) ?? 0) + 1);
        }

        return map;
    }

    private descriptionCounts(): Map<string, number> {
        const map = new Map<string, number>();

        for (const c of this.categoryList ?? []) {
            const key = this.norm(c?.description);
            if (key.length === 0) {
                continue;
            }
            map.set(key, (map.get(key) ?? 0) + 1);
        }

        return map;
    }

    private isPatternInvalid(value: string): boolean {
        if (!this.pattern) {
            return false;
        }

        const re = new RegExp(this.pattern);
        return value.length > 0 && !re.test(value);
    }

    public isNameInvalid(i: number): boolean {
        const c = this.categoryList?.[i];
        if (!c) {
            return false;
        }

        if (this.rowActivated[i] !== true) {
            return false;
        }

        if (c?.editable === false) {
            return false;
        }

        const name = (c?.name ?? '').trim();

        if (name.length === 0) {
            return true;
        }

        if (name.length > this.nameMaxLength) {
            return true;
        }

        if (this.isPatternInvalid(name)) {
            return true;
        }

        const counts = this.nameCounts();
        return (counts.get(this.norm(name)) ?? 0) > 1;
    }

    public isDescriptionInvalid(i: number): boolean {
        const c = this.categoryList?.[i];
        if (!c) {
            return false;
        }

        if (this.rowActivated[i] !== true) {
            return false;
        }

        if (c?.editable === false) {
            return false;
        }

        const desc = (c?.description ?? '').trim();

        if (desc.length === 0) {
            return true;
        }

        if (desc.length > this.descriptionMaxLength) {
            return true;
        }

        if (this.isPatternInvalid(desc)) {
            return true;
        }

        const counts = this.descriptionCounts();
        return (counts.get(this.norm(desc)) ?? 0) > 1;
    }
}
